<template>
  <div
    v-style:bg="backgroundColor"
    class="px-4 py-10 md:flex lg:px-10 lg:pl-6"
    data-test-id="collection-vertical-list"
  >
    <div class="flex items-center md:w-1/2">
      <div class="space-y-4">
        <h2 v-if="title" v-style:c="titleColor" :class="titleStyle">
          <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
          <span v-else>{{ title }}</span>
        </h2>
        <p v-if="subtitle" v-style:c="subtitleColor" :class="subtitleStyle">
          <span v-if="$feature.showTitleHtml" v-html="subtitleHtml" />
          <span v-else>{{ subtitle }}</span>
        </p>
        <ul class="space-y-4">
          <li v-for="(item, i) in items" :key="i" class="f-col">
            <vf-link
              v-style:c="textLinkColor"
              class="w-fit hover:c-$ch duration hover:-rotate-3"
              :class="textLinkStyle"
              :data-cta="item.mediaTarget?.delocalizedTracking"
              :style="{ '--ch': textLinkHoverColor || textLinkColor }"
              :to="item.mediaTarget?.url"
              :variant="textLinkUnderline === 'never' ? 'quieter' : textLinkUnderline === 'hover' ? 'quiet' : undefined"
              @focus="activeIndex = i"
              @mouseover="activeIndex = i"
            >
              <span
                v-if="bgv"
                v-style:bgv="bgv"
                class="duration <md:hidden hover:!bg-left"
                :style="{
                  'background-size': '200% 100%',
                  'background-position': activeIndex === i ? '0' : '-100%',
                  'background-clip': 'text',
                  '-webkit-text-fill-color': 'transparent',
                }"
              >
                <span v-if="$feature.showTitleHtml" v-html="item.titleHtml" />
                <span v-else>{{ item.title }}</span>
              </span>
              <span :class="{ 'md:hidden': bgv }">
                <span v-if="$feature.showTitleHtml" v-html="item.titleHtml" />
                <span v-else>{{ item.title }}</span>
              </span>
            </vf-link>
            <vf-link
              v-if="item.richText"
              class="w-fit"
              :to="item.mediaTarget?.url"
              variant="quiet"
              @focus="activeIndex = i"
              @mouseover="activeIndex = i"
            >
              <cms-rich-text :content="{ richText: item.richText }" />
            </vf-link>
          </li>
        </ul>
      </div>
    </div>
    <div class="w-1/2 flex justify-center <md:hidden">
      <div class="relative i-block">
        <base-picture
          v-if="items[0].responsiveMedia.images"
          v-bind="items[0].responsiveMedia.images"
          class="op-0"
          fit="cover"
        />
        <vf-video
          v-else-if="items[0].responsiveMedia.video"
          v-bind="items[0].responsiveMedia.video"
          :autoplay="!lazy"
          class="op-0"
          loop
        />
        <transition class="duration-500" name="fade">
          <base-picture
            v-if="items[activeIndex].responsiveMedia.images"
            v-bind="items[activeIndex].responsiveMedia.images"
            :key="activeIndex"
            class="absolute-0 overflow-hidden rounded-lg"
            fit="cover"
          />
          <div v-else-if="items[activeIndex].responsiveMedia.video" class="absolute-0 overflow-hidden rounded-lg">
            <vf-video
              v-bind="items[activeIndex].responsiveMedia.video"
              ref="videoRef"
              :autoplay="!lazy"
              loop
            />
          </div>
        </transition>
        <cms-base-caption
          v-if="items[activeIndex].captionConfig.hasCaption"
          v-bind="items[activeIndex].captionConfig.captionProps"
        />
        <base-link
          aria-hidden="true"
          class="absolute-0"
          :data-cta="items[activeIndex].mediaTarget?.delocalizedTracking"
          tabindex="-1"
          :to="items[activeIndex].mediaTarget?.url"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { SectionProvider } from '#content/contexts/section'
import type { CollectionContent } from '#types/components/cms/collection'
import { SectionContextKey } from '#content/contexts/section'

const { content } = defineProps<{ content: CollectionContent<any> }>()

const {
  backgroundColor,
  subtitle,
  subtitleHtml,
  subtitleColor,
  subtitleStyle,
  textLinkColor,
  textLinkHoverColor,
  textLinkStyle,
  textLinkUnderline,
  title,
  titleHtml,
  titleColor,
  titleStyle
} = content

const { lazy } = inject(SectionContextKey, {} as SectionProvider)
const { itemsProp } = useAppConfig().components.cms.collection[content.type!] || {}

const items = ((itemsProp ? content[itemsProp] : content.items) || [])
  .map((item) => ({
    ...item,
    responsiveMedia: getResponsiveMedia(item.media),
    captionConfig: getCaptionConfig(item),
  }))

const activeIndex = ref(0)

const bgv = textLinkColor && textLinkHoverColor && Object.values(textLinkColor)?.some((v) => v !== textLinkHoverColor)
  ? Object.keys(textLinkColor).reduce((acc, bp) => ({
      ...acc,
      [bp]: textLinkColor[bp] !== textLinkHoverColor
        ? `linear-gradient(to right, ${textLinkHoverColor}, ${textLinkHoverColor} 50%, ${textLinkColor[bp]} 50%)`
        : textLinkColor[bp]
    }), {})
  : undefined

const videoRef = useTemplateRef('videoRef')

watch(activeIndex, () => videoRef.value?.load())
</script>
